import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { get, post } from "../../api/api"
import { scoreTone } from "../../utils/seo"
import SeoIntelligencePanel from "../../components/SeoIntelligencePanel"

export default function ProjectView() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState("")

  const loadProject = useCallback(async () => {
    const res = await get(`/projects/${id}`)
    setData(res.data)
  }, [id])

  async function analyze() {
    setRunning(true)
    setError("")
    try {
      await post(`/projects/${id}/analyze`, {})
      await loadProject()
    } catch (err) {
      setError(err.response?.data?.error || "Audit failed")
    } finally {
      setRunning(false)
    }
  }

  useEffect(() => {
    loadProject()
      .catch((err) => setError(err.response?.data?.error || "Failed to load project"))
      .finally(() => setLoading(false))
  }, [loadProject])

  if (loading) return <div className="page">Loading project...</div>
  if (error && !data) return <div className="page error">{error}</div>

  const project = data.project
  const report = data.latestReport
  const insights = report?.insights || {}

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{project.name}</h1>
          <p className="muted">{project.domain}</p>
        </div>
        <button onClick={analyze} disabled={running}>
          {running ? "Running audit..." : "Run audit"}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {!report && (
        <div className="card">
          <p>No report yet. Run an audit to generate crawl, keyword and issue data.</p>
        </div>
      )}

      {report && (
        <>
          <div className="grid">
            <div className="card">
              <p className="muted">Score</p>
              <div className={`score-ring small ${scoreTone(report.score)}`} style={{ "--score": report.score }}>
                {report.score}
              </div>
            </div>
            <div className="card">
              <p className="muted">Pages</p>
              <div className="metric">{report.crawl?.crawled || 0}</div>
            </div>
            <div className="card">
              <p className="muted">Issues</p>
              <div className="metric">{report.audit?.summary?.totalIssues || 0}</div>
            </div>
            <div className="card">
              <p className="muted">Keywords</p>
              <div className="metric">{report.keywords?.length || 0}</div>
            </div>
          </div>

          <SeoIntelligencePanel domain={report.domain || project.domain} score={report.score} insights={insights} />

          <div className="card">
            <h2>AI / autopilot suggestions</h2>
            <pre>{JSON.stringify(report.autopilotFixes || report.ai || [], null, 2)}</pre>
          </div>
        </>
      )}
    </div>
  )
}
